import moment from 'moment-timezone';

import api from '@/helpers/api';
import { sanitizeObject } from '@/helpers/object';
import { serializeArrayParams } from '@/helpers/serializeArrayParams';
import { buildOptimisticWorkerDetail } from '@/modules/worker/helpers/buildOptimisticWorkerDetail';
import { normalizeWorkerEnvironmentFromApi } from '@/modules/worker/helpers/normalizeWorkerEnvironmentFromApi';
import { useWorkerProfileOptimisticStore } from '@/modules/worker/store/workerProfileOptimisticStore';
import {
	type AddJobPayload,
	type AddToAgencyPayload,
	type BulkRemoveFromAgencyResult,
	type CreateWorkerData,
	type CvData,
	type DeleteCVParams,
	EGender,
	type FetchWorkersQuery,
	type GenerateCVResponse,
	type GetWorkerIdByEmailParams,
	type GetWorkerIdByEmailResponse,
	type ParsedData,
	type ProfilePreviewResponse,
	type RegistrationCompletionDetails,
	type RegistrationsByStatus,
	type RegistrationsCount,
	type SyncToEvoliaResult,
	type SyncWorkerToEvoliaData,
	type UpdateContractPreferencesPayload,
	type UpdateJobExperiencePayload,
	type UpdateRegistrationStatus,
	type UpdateWorkerMobilityPayload,
	type UpdateWorkerPayload,
	type UpdateWorkerSalaryPayload,
	type UpdateWorkerSchedulePayload,
	type UpdateWorkPreferencesPayload,
	type WorkerDetail,
	type WorkerListItem,
	type WorkerMissionMetadatas,
	type WorkerMissionMetadatasItem,
	type WorkerMissionMetadatasResponse,
	type WorkerMissionsFilters,
	type WorkerMissionsFiltersApiParams,
	type WorkerMissionsListApiParams,
	type WorkerMissionsListQueryParams,
	type WorkersMetadata,
	type WorkersMetadataQuery,
} from '@/modules/worker/types';
import {
	Gender,
	type IIdentificateResultCommonWorkerSchema,
	type IRegularAvailabilities,
} from '@/types/worker';

import type { Period } from '@/modules/companyNeed/components/modals/createOrder/types';
import type {
	PlanningResponse,
	WorkerPlanningFilters,
	WorkerPlanningListResponse,
} from '@/modules/worker/types/workerPlanning';
import type { Pagination } from '@/types/common';
import type { IJobStored } from '@/types/job';
import type { DuplicateWorkerResponse, SearchDuplicatesRequest } from './types';

export interface CreateWorkerOptions {
	jobsFromForm?: IJobStored[];
}

const isNotFoundError = (error: unknown): boolean => {
	const err = error as
		| { response?: { status?: number }; status?: number }
		| undefined;
	return err?.response?.status === 404 || err?.status === 404;
};

export interface CreateWorkerOptions {
	jobsFromForm?: IJobStored[];
}

export interface InterimMissionsResponse {
	agencyId: string | null;
	agencyName: string | null;
	besoinId: string;
	besoinName: string | null;
	companyId: string;
	companyName: string;
	contractType: string | null;
	jobId: string | null;
	jobName: string | null;
	missionId: string;
	missionName: string;
	missionStatus: string;
	profileUpdatedAt: string;
	missionCreatedAt: string | null;
	periods?: Period[];
	startAt: string;
	workerMissionStatus: string;
}

/** API sends `lastContactAt` as null or an ISO 8601 string. */
const parseLastContactAtFromApi = (raw: unknown): Date | null => {
	if (raw === undefined || raw === null || raw === '') return null;
	if (typeof raw !== 'string') return null;
	const trimmed = raw.trim();
	if (trimmed.length === 0) return null;
	const parsed = moment(trimmed);
	return parsed.isValid() ? parsed.toDate() : null;
};

const mapWorkerFromApi = (worker: Record<string, unknown>) => {
	const mapped: Record<string, unknown> = { ...worker };

	// jobs: [{jobId, jobName, ...}] → jobNames: [{id, name}[]] + jobIds: string[]
	if (Array.isArray(worker.jobs)) {
		mapped.jobNames = worker.jobs.map((j: Record<string, unknown>) => ({
			id: j.jobId,
			name: j.jobName,
		}));
		mapped.jobIds = worker.jobs.map((j: Record<string, unknown>) => j.jobId);
	}

	// hasVehicle → vehicle
	if (worker.hasVehicle !== undefined) {
		mapped.vehicle = worker.hasVehicle;
	}

	// agencies → agenciesList
	if (worker.agencies && !worker.agenciesList) {
		mapped.agenciesList = worker.agencies;
	}

	// citySlug → addressCity (capitalize)
	if (worker.citySlug && !worker.addressCity) {
		const slug = worker.citySlug as string;
		mapped.addressCity = slug.charAt(0).toUpperCase() + slug.slice(1);
	}

	// location "lat,lng" string → latitude/longitude numbers
	if (typeof worker.location === 'string' && worker.location.includes(',')) {
		const [lat, lng] = worker.location.split(',').map(Number);
		if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
			mapped.latitude = lat;
			mapped.longitude = lng;
		}
	}

	if (
		worker.environment &&
		typeof worker.environment === 'object' &&
		!Array.isArray(worker.environment)
	) {
		mapped.environment = normalizeWorkerEnvironmentFromApi(
			worker.environment as Record<string, unknown>
		);
	}

	// gender: Gender.Male/Gender.Female → EGender.MALE/EGender.FEMALE
	switch (worker.gender) {
		case Gender.Male:
			mapped.gender = EGender.MALE;
			break;
		case Gender.Female:
			mapped.gender = EGender.FEMALE;
			break;
		default:
			mapped.gender = undefined;
			break;
	}

	// worker-query returns `blacklists`, while the legacy front expects `blacklist`.
	if (Array.isArray(worker.blacklists) && !mapped.blacklist) {
		mapped.blacklist = worker.blacklists.map(
			(item: Record<string, unknown>) => ({
				companyId: String(item.companyId ?? ''),
				companyName:
					typeof item.companyName === 'string' ? item.companyName : '',
				from: typeof item.origin === 'string' ? item.origin : '',
			})
		);
	}

	if (
		worker.workerStatus !== undefined &&
		mapped.mobileAppStatus === undefined
	) {
		mapped.mobileAppStatus = worker.workerStatus;
	}
	if (
		worker.mobile_app_status !== undefined &&
		mapped.mobileAppStatus === undefined
	) {
		mapped.mobileAppStatus = worker.mobile_app_status;
	}

	if (Array.isArray(mapped.agencies)) {
		mapped.agencies = (mapped.agencies as Record<string, unknown>[]).map(
			(agency) => {
				const next = { ...agency };
				if (
					next.workerStatus !== undefined &&
					next.mobileAppStatus === undefined
				) {
					next.mobileAppStatus = next.workerStatus;
				}
				return next;
			}
		);
	}

	const nonDelegableReasonRaw =
		worker.nonDelegableReason ??
		worker.refusalReason ??
		worker.inactivityReason ??
		worker.nonDelegableLabel ??
		worker.delegabilityReason;
	if (
		typeof nonDelegableReasonRaw === 'string' &&
		nonDelegableReasonRaw.trim().length > 0
	) {
		mapped.nonDelegableReason = nonDelegableReasonRaw.trim();
	}

	const deactivationReasonIdRaw =
		worker.deactivationReasonId ?? worker.deactivation_reason_id;
	if (
		deactivationReasonIdRaw !== undefined &&
		deactivationReasonIdRaw !== null &&
		String(deactivationReasonIdRaw).trim() !== ''
	) {
		mapped.deactivationReasonId = String(deactivationReasonIdRaw).trim();
	}

	if (worker.delegable !== undefined) {
		mapped.delegable =
			worker.delegable === false || worker.delegable === 'false'
				? false
				: Boolean(worker.delegable);
	}

	const besoinsRaw = worker.besoins;
	if (Array.isArray(besoinsRaw) && besoinsRaw.length > 0) {
		mapped.besoins = besoinsRaw.map((item: Record<string, unknown>) => ({
			jobId: String(item.jobId ?? ''),
			companyId: String(item.companyId ?? ''),
			companyName: typeof item.companyName === 'string' ? item.companyName : '',
			besoinWorkerStatus:
				typeof item.besoinWorkerStatus === 'string'
					? item.besoinWorkerStatus
					: '',
			agencyId: String(item.agencyId ?? ''),
			agencyName: typeof item.agencyName === 'string' ? item.agencyName : '',
		}));
	}

	const lastContactRaw = worker.lastContactAt ?? worker.last_contact_at;
	mapped.lastContactAt = parseLastContactAtFromApi(lastContactRaw);

	if (mapped.delegable === undefined) {
		mapped.delegable = true;
	}

	return mapped;
};

// --- GET ---

const loadWorkers = async (
	params: FetchWorkersQuery
): Promise<Pagination<WorkerListItem>> => {
	const { mobileAppStatuses, ...rest } = params;
	const requestParams: Record<string, unknown> = { ...rest };
	if (mobileAppStatuses?.length) {
		requestParams.mobile_app_status = mobileAppStatuses;
	}
	const response = await api.request<Pagination<Record<string, unknown>>>({
		url: '/workers/search',
		method: 'GET',
		params: requestParams,
	});
	const data = response.data as unknown as Pagination<WorkerListItem>;
	if (Array.isArray(data?.items)) {
		data.items = data.items.map((item) => {
			const raw = item as unknown as Record<string, unknown>;
			return {
				...mapWorkerFromApi(raw),
				hasPendingAskedChanges: !!raw.hasPendingAskedChanges,
				hasPendingPaymentRequest: !!raw.hasPendingPaymentRequest,
				hasLeftRegistrationsTracking: !!raw.hasLeftRegistrationsTracking,
			} as unknown as WorkerListItem;
		});
	}
	return data;
};

const fetchWorkersMetadata = async (
	params?: WorkersMetadataQuery
): Promise<WorkersMetadata> => {
	const { mobileAppStatuses, ...rest } = params ?? {};
	const requestParams: Record<string, unknown> = { ...rest };
	if (mobileAppStatuses?.length) {
		requestParams.mobile_app_status = mobileAppStatuses;
	}
	const response = await api.request({
		url: '/workers/search/metadata',
		method: 'GET',
		params: requestParams,
	});
	return response.data;
};

const getAllWorkers = async (
	params: Record<string, unknown>
): Promise<Pagination<WorkerListItem>> => {
	const response = await api.request({
		url: '/workers',
		method: 'GET',
		params,
	});
	const result = response.data;
	if (Array.isArray(result?.data)) {
		result.data = result.data.map(mapWorkerFromApi);
	} else if (Array.isArray(result?.items)) {
		result.items = result.items.map(mapWorkerFromApi);
	}
	return result;
};

const getWorker = async (workerId: string): Promise<WorkerDetail> => {
	try {
		const response = await api.request({
			url: `/workers/${workerId}`,
			method: 'GET',
		});
		const worker = mapWorkerFromApi(
			response.data as Record<string, unknown>
		) as unknown as WorkerDetail;
		useWorkerProfileOptimisticStore().clearWorker(workerId);
		return worker;
	} catch (error: unknown) {
		const optimisticWorker =
			useWorkerProfileOptimisticStore().getWorker(workerId);
		if (optimisticWorker && isNotFoundError(error)) {
			return optimisticWorker;
		}
		throw error;
	}
};

const getProfilePreview = async (
	workerId: string
): Promise<ProfilePreviewResponse> => {
	const response = await api.request<ProfilePreviewResponse>({
		url: `/workers/${workerId}/profile-preview`,
		method: 'GET',
	});
	return response.data;
};

const getCvs = async (workerId: string): Promise<CvData[]> => {
	const response = await api.request<CvData[]>({
		url: `/workers/${workerId}/cvs`,
		method: 'GET',
	});
	return response.data;
};

const buildWorkerMissionsFilterParams = ({
	companyId,
	search,
	status,
	workerStatus,
	jobId,
	contractType,
}: WorkerMissionsFilters): WorkerMissionsFiltersApiParams => ({
	companyId,
	search,
	status,
	workerStatus: workerStatus?.length ? JSON.stringify(workerStatus) : undefined,
	jobId,
	contractType,
});

const getInterimMissions = async (
	workerId: string,
	queryParams: WorkerMissionsListQueryParams
): Promise<Pagination<InterimMissionsResponse>> => {
	const { page, limit, sortField, sortOrder, ...filters } = queryParams;
	const response = await api.request({
		url: `/workers/${workerId}/missions`,
		method: 'GET',
		params: sanitizeObject<WorkerMissionsListApiParams>({
			page,
			limit,
			sortBy: sortField || undefined,
			sortOrder: sortField ? (sortOrder === 1 ? 'asc' : 'desc') : undefined,
			...buildWorkerMissionsFilterParams(filters),
		}),
	});
	return response.data;
};

const metadataRecordToItems = (
	record: Partial<Record<string, number>>
): WorkerMissionMetadatasItem[] =>
	Object.entries(record).flatMap(([key, value]) =>
		typeof value === 'number' ? [{ key, count: value }] : []
	);

const mapWorkerMissionMetadatasResponse = (
	payload: WorkerMissionMetadatasResponse
): WorkerMissionMetadatas => ({
	missionStatus: metadataRecordToItems(payload.missionStatus),
	interimStatus: metadataRecordToItems(payload.workerStatus),
	jobs: (payload.job ?? []).map((entry) => ({
		key: entry.jobId,
		count: entry.count,
	})),
	contractType: metadataRecordToItems(payload.contractType),
});

const getInterimMissionsMetaDatas = async (
	workerId: string,
	queryParams: WorkerMissionsFilters
): Promise<WorkerMissionMetadatas> => {
	const response = await api.request<WorkerMissionMetadatasResponse>({
		url: `/workers/${workerId}/missions/metadata`,
		method: 'GET',
		params: sanitizeObject<WorkerMissionsFiltersApiParams>(
			buildWorkerMissionsFilterParams(queryParams)
		),
	});
	return mapWorkerMissionMetadatasResponse(response.data);
};

const loadInterimMissions = async (data: {
	interimId: string;
	missionId: string;
}) => {
	const response = await api.request({
		url: `/workers/${data.interimId}/${data.missionId}`,
		method: 'GET',
	});
	return response.data;
};

const loadInterimsToBeInactive = async (params: Record<string, unknown>) => {
	const response = await api.request({
		url: '/workers/toBeInactive',
		method: 'GET',
		params,
	});
	return response.data;
};

const getEvaluations = async (data: { interimId: string }) => {
	const response = await api.request({
		url: `/workers/${data.interimId}/evaluations`,
		method: 'GET',
	});
	return response.data;
};

const getInterimUnavailabilities = async (data: {
	interimId: string;
	ranges?: Record<string, unknown>;
}) => {
	const response = await api.request({
		url: `/workers/${data.interimId}/unavailabilities`,
		method: 'GET',
		params: data.ranges,
	});
	return response.data;
};

const getInterimsAvailabilities = async ({
	mobileAppStatuses,
	...rest
}: WorkerPlanningFilters & {
	startDate: number;
	endDate: number;
	page: number;
}): Promise<WorkerPlanningListResponse> => {
	const params: Record<string, unknown> = { ...rest };
	if (mobileAppStatuses?.length) params.mobile_app_status = mobileAppStatuses;
	const response = await api.request<Partial<WorkerPlanningListResponse>>({
		url: '/workers/planning',
		method: 'GET',
		params,
		paramsSerializer: () => serializeArrayParams(params),
	});
	return {
		total: response.data?.total ?? 0,
		items: (response.data?.items ?? []).map((item) => ({
			...item,
			regularAvailabilities: item.regularAvailabilities ?? [],
			unavailabilities: item.unavailabilities ?? [],
			missions: item.missions ?? [],
		})),
	};
};

const getWorkerPlanning = async (
	workerId: string,
	startDate: number,
	endDate: number
): Promise<PlanningResponse> => {
	const response = await api.request<PlanningResponse>({
		url: `/workers/${workerId}/planning`,
		method: 'GET',
		params: { startDate, endDate },
	});
	return response.data;
};

const getRegistrations = async (): Promise<RegistrationsByStatus> => {
	const response = await api.request({
		url: '/workers/registrations/tracking',
		method: 'GET',
	});
	return response.data;
};

const getRegistrationCompletionDetails = async (
	workerId: string
): Promise<RegistrationCompletionDetails> => {
	const response = await api.request<RegistrationCompletionDetails>({
		url: `/workers/${workerId}/registration/completion-details`,
		method: 'GET',
	});
	return response.data;
};

const getRegistrationsCount = async (): Promise<RegistrationsCount> => {
	const response = await api.request({
		url: '/workers/registrations/count',
		method: 'GET',
	});
	return response.data;
};

const mapDuplicateToCommonWorker = (
	w: DuplicateWorkerResponse
): IIdentificateResultCommonWorkerSchema => ({
	id: w.id,
	firstname: w.firstname,
	lastname: w.lastname,
	email: w.email || '',
	phone: w.phone || '',
	birthDate: w.birthDate ? new Date(w.birthDate).getTime() : 0,
	birthPlace: w.birthPlace || '',
	nir: w.nir || '',
	nirKey: '',
	nationalityCode: '',
	birthCountry: '',
	picture: w.picture || '',
	// biome-ignore lint/suspicious/noExplicitAny: Init Biome
	address: { city: '', citySlug: '', countryCode: '' } as any,
	agencies: w.agencies.map((a) => ({
		id: a.agencyId,
		name: a.agencyName || '',
		deleted: false,
		inactive: false,
	})),
	// biome-ignore lint/suspicious/noExplicitAny: Init Biome
	databaseOrigin: (w.workerType?.toLowerCase() || 'interim') as any,
	evoliaId: w.evoliaId?.toString(),
	nameSlugScore: w.nameSlugScore,
	matchingScore: w.matchReasons.length,
	// biome-ignore lint/suspicious/noExplicitAny: Init Biome
	jobs: w.jobs.map((j) => ({ id: j.jobId, name: j.jobLabel || '' })) as any,
	cv: undefined,
	source: '',
	jobName: w.jobs[0]?.jobLabel || '',
	jobOfferName: '',
	duration: 0,
	scenario: w.scenario,
	action: w.action,
	matchReasons: w.matchReasons,
	updateProfileData: w.updateProfileData,
});

const searchDuplicates = async (
	data: SearchDuplicatesRequest
): Promise<IIdentificateResultCommonWorkerSchema[]> => {
	const response = await api.request({
		url: '/workers/search/duplicates',
		method: 'GET',
		params: {
			firstname: data.firstname,
			lastname: data.lastname,
			email: data.email,
			phone: data.phone,
			nir: data.nir,
			birthDate: data.birthDate,
			excludeWorkerId: data.excludeWorkerId,
		},
	});
	return (response.data as DuplicateWorkerResponse[]).map(
		mapDuplicateToCommonWorker
	);
};

const getWorkerIdByEmail = async (
	params: GetWorkerIdByEmailParams
): Promise<GetWorkerIdByEmailResponse> => {
	const response = await api.request<{ workerId: string }>({
		url: '/workers/by-email',
		method: 'GET',
		params,
	});
	return response.data;
};

// --- PATCH ---

const patchWorker = async (workerId: string, data: UpdateWorkerPayload) => {
	const response = await api.request({
		url: `/workers/${workerId}`,
		method: 'PATCH',
		data,
	});
	return response.data;
};

const updatePersonalInfo = async (
	workerId: string,
	data: Record<string, unknown>
) => {
	const response = await api.request({
		url: `/workers/${workerId}/personal-info`,
		method: 'PATCH',
		data,
	});
	return response.data;
};

const patchRegularAvailabilities = async (
	workerId: string,
	data: { regularAvailabilities: IRegularAvailabilities[] }
) => {
	await api.request({
		url: `/workers/${workerId}/regular-availabilities`,
		method: 'PATCH',
		data,
	});
};

const patchWorkPreferences = async (
	workerId: string,
	data: UpdateWorkPreferencesPayload
) => {
	await api.request({
		url: `/workers/${workerId}/work-preferences`,
		method: 'PATCH',
		data,
	});
};

const patchWorkerKeywords = async (
	workerId: string,
	data: { keywords: string[] }
) => {
	await api.request({
		url: `/workers/${workerId}/keywords`,
		method: 'PATCH',
		data,
	});
};

const patchWorkerMobility = async (
	workerId: string,
	data: UpdateWorkerMobilityPayload
) => {
	const response = await api.request({
		url: `/workers/${workerId}/mobility`,
		method: 'PATCH',
		data,
	});
	return response.data;
};

const patchContractPreferences = async (
	workerId: string,
	data: UpdateContractPreferencesPayload
) => {
	await api.request({
		url: `/workers/${workerId}/contract-preferences`,
		method: 'PATCH',
		data,
	});
};

const patchWorkerSchedule = async (
	workerId: string,
	data: UpdateWorkerSchedulePayload
) => {
	await api.request({
		url: `/workers/${workerId}/schedule`,
		method: 'PATCH',
		data,
	});
};

const patchWorkerSalary = async (
	workerId: string,
	data: UpdateWorkerSalaryPayload
) => {
	await api.request({
		url: `/workers/${workerId}/salary`,
		method: 'PATCH',
		data,
	});
};

const patchJobExperience = async (
	workerId: string,
	jobId: string,
	data: UpdateJobExperiencePayload
) => {
	await api.request({
		url: `/workers/${workerId}/jobs/${jobId}/experience`,
		method: 'PATCH',
		data,
	});
};

// --- DELETE ---

const removeFromAgency = async (workerId: string, agencyId: string) => {
	await api.request({
		url: `/workers/${workerId}/agencies/${agencyId}`,
		method: 'DELETE',
	});
};

const bulkRemoveFromAgency = async (
	agencyId: string,
	workerIds: string[]
): Promise<BulkRemoveFromAgencyResult> => {
	const response = await api.request<BulkRemoveFromAgencyResult>({
		url: `/workers/agencies/${agencyId}`,
		method: 'DELETE',
		data: { workerIds },
	});
	return response.data;
};

const deleteJob = async (workerId: string, jobId: string) => {
	await api.request({
		url: `/workers/${workerId}/jobs/${jobId}`,
		method: 'DELETE',
	});
};

const deleteCV = async (workerId: string, params: DeleteCVParams) => {
	await api.request({
		url: `/workers/${workerId}/documents/cv`,
		method: 'DELETE',
		params,
	});
};

const deleteDocument = async (workerId: string, documentId: string) => {
	await api.request({
		url: `/workers/${workerId}/documents/${documentId}`,
		method: 'DELETE',
	});
};

const deleteLongUnavailability = async (
	interimId: string,
	startDate: string,
	endDate: string
) => {
	await api.request({
		url: `/workers/${interimId}/unavailabilities/long`,
		method: 'DELETE',
		params: { startDate, endDate },
	});
};

// --- PATCH ---

const UPDATE_WORKER_FULL_ALLOWED_FIELDS = [
	'firstname',
	'lastname',
	'birthName',
	'gender',
	'birthday',
	'nationality',
	'birthplace',
	'birthCountry',
	'email',
	'phone',
	'address',
	'nir',
	'nirKey',
	'vehicle',
	'carpooling',
	'maxTravelDistance',
	'maxTravelTime',
	'nightShift',
	'weekendShift',
	'regularAvailabilities',
	'contractPreference',
	'schedule',
	'teamPreference',
	'environment',
	'expectedHourlySalary',
	'referenceCheck',
] as const;

const buildUpdateWorkerFullPayload = (data: Record<string, unknown>) => {
	const payload: Record<string, unknown> = {};

	for (const key of UPDATE_WORKER_FULL_ALLOWED_FIELDS) {
		if (data[key] !== undefined) payload[key] = data[key];
	}

	if (data.birthDate !== undefined && payload.birthday === undefined) {
		const ts =
			typeof data.birthDate === 'number'
				? data.birthDate
				: Date.parse(String(data.birthDate));
		if (!Number.isNaN(ts)) payload.birthday = new Date(ts).toISOString();
	}

	if (data.birthPlace !== undefined && payload.birthplace === undefined) {
		payload.birthplace = data.birthPlace;
	}

	if (data.nationalityCode !== undefined && payload.nationality === undefined) {
		payload.nationality = data.nationalityCode;
	}

	if (
		data.salaryExpected !== undefined &&
		payload.expectedHourlySalary === undefined
	) {
		payload.expectedHourlySalary = data.salaryExpected;
	}

	return payload;
};

const editInterim = async (data: Record<string, unknown>) => {
	const payload = buildUpdateWorkerFullPayload(data);
	const response = await api.request({
		url: `/workers/${data.id}`,
		method: 'PATCH',
		data: payload,
	});
	return response.data;
};

const saveUnavailability = async (data: {
	interimId: string;
	data: Record<string, unknown>;
}) => {
	const response = await api.request({
		url: `/workers/${data.interimId}/unavailabilities/daily`,
		method: 'PUT',
		data: data.data,
	});
	return response.data;
};

const saveLongUnavailability = async (data: {
	interimId: string;
	data: Record<string, unknown>;
}) => {
	const response = await api.request({
		url: `/workers/${data.interimId}/unavailabilities/long`,
		method: 'PUT',
		data: data.data,
	});
	return response.data;
};

const changeInterimActivity = async (data: {
	interimId: string;
	status: string;
	reason?: string;
	reasonComplement?: string;
}) => {
	const response = await api.request({
		url: `/workers/${data.interimId}/status`,
		method: 'PUT',
		data: {
			status: data.status,
			inactivityReason: data.reason,
			reasonComplement: data.reasonComplement,
		},
	});
	return response.data;
};

// --- POST ---

const changeInterimActivityGroup = async (data: Record<string, unknown>) => {
	const response = await api.request({
		url: '/workers/status',
		method: 'POST',
		data,
	});
	return response.data;
};

const deactivateWorkers = async (
	workerIds: string[],
	reasonId: string,
	removeMission: boolean,
	comment?: string
) => {
	const response = await api.request({
		url: '/workers/deactivate',
		method: 'POST',
		data: {
			workerIds,
			reasonId,
			removeMission,
			...(comment ? { comment } : {}),
		},
	});
	return response.data;
};

const reactivateWorkers = async (workerIds: string[]) => {
	const response = await api.request({
		url: '/workers/reactivate',
		method: 'POST',
		data: { workerIds },
	});
	return response.data;
};

const sendInterimInvitationOne = async (data: { interimId: string }) => {
	const response = await api.request({
		url: `/workers/${data.interimId}/mobile/invite`,
		method: 'POST',
	});
	return response.data;
};

const sendInterimInvitation = async () => {
	const response = await api.request({
		url: '/workers/mobile/invite-all',
		method: 'POST',
	});
	return response.data;
};

const resendInterimInvitationOne = async (data: { interimId: string }) => {
	const response = await api.request({
		url: `/workers/${data.interimId}/mobile/relaunch`,
		method: 'POST',
	});
	return response.data;
};

const relaunchInterimCompletionFile = async (data: { interimId: string }) => {
	const response = await api.request({
		url: `/workers/${data.interimId}/mobile/relaunch/completion-file`,
		method: 'POST',
	});
	return response.data;
};

const resendInterimInvitation = async () => {
	const response = await api.request({
		url: '/workers/mobile/relaunch-all',
		method: 'POST',
	});
	return response.data;
};

const getInterimsAvailabilityForPeriods = async (
	data: Record<string, unknown>
) => {
	const response = await api.request({
		url: '/workers/availability',
		method: 'POST',
		data,
	});
	return response.data;
};

const getEvoliaInterim = async (data: Record<string, unknown>) => {
	const response = await api.request({
		url: '/workers/get-evolia-interim',
		method: 'POST',
		data,
	});
	return response.data;
};

const importEvoliaInterim = async (data: Record<string, unknown>) => {
	const response = await api.request({
		url: '/workers/synchronize-with-evolia',
		method: 'POST',
		data,
	});
	return response.data;
};

const syncWorkerToEvolia = async (
	workerId: string,
	data: SyncWorkerToEvoliaData
): Promise<SyncToEvoliaResult> => {
	const response = await api.request({
		url: `/workers/${workerId}/commands/sync-to-evolia`,
		method: 'POST',
		data,
	});
	return response.data;
};

const parseResume = async (cv: string): Promise<ParsedData> => {
	const response = await api.request({
		url: '/resume/parse',
		method: 'POST',
		data: { cv },
	});
	return response.data;
};

const generateCV = async (
	workerId: string,
	anonymize: boolean
): Promise<GenerateCVResponse> => {
	const response = await api.request({
		url: `/workers/${workerId}/documents/generate-cv`,
		method: 'POST',
		data: { anonymize },
	});
	return response.data;
};

const createWorker = async (
	data: CreateWorkerData,
	options?: CreateWorkerOptions
) => {
	const { cv, ...rest } = data;
	const cvPath = cv != null ? String(cv.path ?? '').trim() : '';
	const payload = cvPath ? { ...rest, cv: { path: cvPath } } : rest;

	const response = await api.request({
		url: '/workers',
		method: 'POST',
		data: payload,
	});
	const commandResponse = response.data as Record<string, unknown>;

	if (commandResponse.id) {
		const optimisticWorker = buildOptimisticWorkerDetail(
			data,
			commandResponse,
			options?.jobsFromForm ?? []
		);
		useWorkerProfileOptimisticStore().seedWorker(optimisticWorker);
	}

	return response.data;
};

const relaunchCandidateForRegistration = async (workerId: string) => {
	const response = await api.request({
		url: `/workers/${workerId}/registration/relaunch`,
		method: 'POST',
	});
	return response.data;
};

const updateRegistrationStatus = async (
	workerId: string,
	status: UpdateRegistrationStatus
) => {
	const response = await api.request({
		url: `/workers/${workerId}/registration/status/update`,
		method: 'POST',
		data: { status },
	});
	return response.data;
};

const validateRegistrationFile = async (workerId: string) => {
	const response = await api.request({
		url: `/workers/${workerId}/registration-file/validate`,
		method: 'POST',
	});
	return response.data;
};

const removeFromRegistrationsTracking = async (
	workerId: string
): Promise<void> => {
	await api.request({
		url: `/workers/${workerId}/commands/remove-from-registrations-tracking`,
		method: 'POST',
	});
};

const sendNewPasswordSms = async (
	workerId: string,
	data: { phone: string; email: string; firstName?: string; lastName?: string }
): Promise<void> => {
	await api.request({
		url: `/auth/workers/${workerId}/commands/send-new-password-sms`,
		method: 'POST',
		data,
	});
};

const addToRegistrationsTracking = async (workerId: string): Promise<void> => {
	await api.request({
		url: `/workers/${workerId}/commands/add-to-registrations-tracking`,
		method: 'POST',
	});
};

const addJob = async (workerId: string, data: AddJobPayload) => {
	const response = await api.request({
		url: `/workers/${workerId}/jobs`,
		method: 'POST',
		data,
	});
	return response.data;
};

const addToAgency = async (workerId: string, data: AddToAgencyPayload) => {
	const response = await api.request({
		url: `/workers/${workerId}/agencies`,
		method: 'POST',
		data,
	});
	return response.data;
};

// biome-ignore lint/suspicious/noExplicitAny: Init Biome
const shareWorker = async (data: any) => {
	const response = await api.request({
		url: '/identification/share-worker',
		method: 'POST',
		data,
	});
	return response.data;
};

export default {
	// GET
	loadWorkers,
	fetchWorkersMetadata,
	getAllWorkers,
	getWorker,
	getProfilePreview,
	getCvs,
	getInterimMissions,
	getInterimMissionsMetaDatas,
	loadInterimMissions,
	loadInterimsToBeInactive,
	getEvaluations,
	getInterimUnavailabilities,
	getInterimsAvailabilities,
	getWorkerPlanning,
	getRegistrations,
	getRegistrationCompletionDetails,
	getRegistrationsCount,
	searchDuplicates,
	getWorkerIdByEmail,

	// PATCH
	patchWorker,
	updatePersonalInfo,
	patchRegularAvailabilities,
	patchWorkPreferences,
	patchWorkerKeywords,
	patchWorkerMobility,
	patchContractPreferences,
	patchWorkerSchedule,
	patchWorkerSalary,
	patchJobExperience,

	// DELETE
	removeFromAgency,
	bulkRemoveFromAgency,
	deleteJob,
	deleteCV,
	deleteDocument,
	deleteLongUnavailability,

	// PUT
	editInterim,
	saveUnavailability,
	saveLongUnavailability,
	changeInterimActivity,

	// POST
	changeInterimActivityGroup,
	deactivateWorkers,
	reactivateWorkers,
	sendInterimInvitationOne,
	sendInterimInvitation,
	resendInterimInvitationOne,
	relaunchInterimCompletionFile,
	resendInterimInvitation,
	getInterimsAvailabilityForPeriods,
	getEvoliaInterim,
	importEvoliaInterim,
	syncWorkerToEvolia,
	parseResume,
	generateCV,
	createWorker,
	relaunchCandidateForRegistration,
	updateRegistrationStatus,
	validateRegistrationFile,
	removeFromRegistrationsTracking,
	addToRegistrationsTracking,
	sendNewPasswordSms,
	addJob,
	addToAgency,
	shareWorker,
};
